/**
 * useEilandersLookup.js
 *
 * Laadt eilanders.json éénmalig en biedt een snelle naam-lookup.
 * Vervangt de 14-fetches-per-deelnemer aanpak van fetchOldResultsForParticipant.
 *
 * Aanpak:
 * - eilanders.json wordt module-level gecached (singleton) zodat meerdere
 *   componenten dezelfde data delen zonder opnieuw te fetchen
 * - Naam-matching via normNaamCompact (zelfde pipeline als gen-eilanders.js)
 */

import { useState, useEffect, useMemo } from 'react';
import { normNaamCompact } from '../utils/normNaam.js';

const PLUGIN_URL = typeof window !== 'undefined' && window.EUREKA_PLUGIN_URL
  ? window.EUREKA_PLUGIN_URL : '/';
const EILANDERS_URL = `${PLUGIN_URL}uitslagen-output/eilanders.json`;

// Module-level singleton — wordt één keer geladen, daarna gedeeld
let eilandersCache = null;      // null = nog niet geladen, false = bezig, object = geladen
let eilandersCallbacks = [];    // wachtrij van callbacks die wachten op de data

function laadEilanders() {
  if (eilandersCache) return Promise.resolve(eilandersCache);
  if (eilandersCache === false) {
    // Al bezig met laden — geef een promise terug die wacht op het resultaat
    return new Promise((resolve) => { eilandersCallbacks.push(resolve); });
  }

  eilandersCache = false; // markeer als "bezig"
  return fetch(EILANDERS_URL)
    .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
    .then((data) => {
      eilandersCache = data;
      eilandersCallbacks.forEach((cb) => cb(data));
      eilandersCallbacks = [];
      return data;
    })
    .catch((e) => {
      eilandersCache = null; // reset zodat retry mogelijk is
      eilandersCallbacks.forEach((cb) => cb(null));
      eilandersCallbacks = [];
      throw e;
    });
}

/**
 * Bouwt een lookup-map: normNaamCompact(naam) → edities array
 * zodat opzoeken O(1) is i.p.v. O(n) per deelnemer.
 */
function bouwLookupMap(eilanders) {
  const map = new Map();
  for (const e of eilanders) {
    const key = normNaamCompact(e.naam);
    if (key) map.set(key, { naam: e.naam, edities: e.edities || [] });
  }
  return map;
}

/**
 * Hook: laadt eilanders.json eenmalig en geeft een lookup-functie terug.
 *
 * Gebruik:
 *   const { zoekEdities, loading } = useEilandersLookup();
 *   const edities = zoekEdities(participant);  // geeft [] als niet gevonden
 */
export function useEilandersLookup() {
  const [data, setData] = useState(eilandersCache || null);
  const [loading, setLoading] = useState(!eilandersCache);
  const [fout, setFout] = useState(null);

  useEffect(() => {
    if (eilandersCache && eilandersCache !== false) {
      setData(eilandersCache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    laadEilanders()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setFout(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Bouw lookup-map alleen als data verandert
  const lookupMap = useMemo(() => data ? bouwLookupMap(data.eilanders || []) : null, [data]);

  /**
   * Zoekt edities voor een deelnemer op basis van naam-varianten.
   * Geeft array van { year, totaaltijd, plaats, type } objecten.
   */
  function zoekEdities(participant) {
    if (!lookupMap) return [];
    const naam = participant.Naam || participant.naam || '';
    const voornaam = participant.Voornaam || '';
    const tv = participant.Tussenvoegsel || '';
    const achternaam = participant.Achternaam || '';

    const varianten = [naam];
    if (voornaam && achternaam) {
      varianten.push(`${voornaam}${tv ? ` ${tv}` : ''} ${achternaam}`.trim());
      varianten.push(`${achternaam}, ${voornaam}${tv ? ` ${tv}` : ''}`.trim());
    }

    for (const variant of varianten) {
      const key = normNaamCompact(variant);
      const gevonden = lookupMap.get(key);
      if (gevonden) {
        return gevonden.edities.map((e) => ({
          year: e.jaar,
          totaaltijd: e.totaaltijd,
          plaats: e.plaats,
          type: e.type,
        }));
      }
    }
    return [];
  }

  return { zoekEdities, loading, fout, aantalEilanders: data?.aantalEilanders };
}
